import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SHOW_READINESS } from "@/lib/hooks";
import { AuthProvider } from "@/context/AuthContext";
import { BrandProvider, useBrand } from "@/context/BrandContext";
import { Header } from "@/components/AppShell";
import { ProtectedRoute, Loader } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import ModuleView from "@/pages/ModuleView";
import LessonReader from "@/pages/LessonReader";
import Quiz from "@/pages/Quiz";
import Glossary from "@/pages/Glossary";
import Capstone from "@/pages/Capstone";
import Certificates, { CertificateView } from "@/pages/Certificates";
import Verify from "@/pages/Verify";
import EditionLink from "@/pages/EditionLink";
import Admin from "@/pages/Admin";
import EditionEditor from "@/pages/EditionEditor";
import LabPlayer from "@/pages/LabPlayer";
import Readiness from "@/pages/Readiness";
import Learn from "@/pages/Learn";
import PlantProject from "@/pages/PlantProject";
import ChangePassword from "@/pages/ChangePassword";
import SetPassword from "@/pages/SetPassword";
import ChooseTrack from "@/pages/ChooseTrack";
import ExecutiveSession from "@/pages/ExecutiveSession";
import ExecutiveCheck from "@/pages/ExecutiveCheck";

const P = (el, admin = false) => <ProtectedRoute admin={admin}>{el}</ProtectedRoute>;

const Shell = () => {
  const { edition, theme } = useBrand();
  const location = useLocation();
  if (!edition && !location.pathname.startsWith("/e/")) return (
    <div data-testid="edition-skeleton" className="animate-pulse">
      <div className="h-16 border-b border-border bg-muted/40" />
      <div className="mx-auto max-w-6xl space-y-4 px-4 py-14 sm:px-8"><div className="h-4 w-40 bg-muted" /><div className="h-10 w-2/3 bg-muted" /><div className="h-4 w-1/2 bg-muted" /><div className="mt-8 h-48 bg-muted" /></div>
    </div>
  );
  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-card focus:p-2">Skip to content</a>
      <Header />
      <div id="main">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/e/:slug" element={<EditionLink />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/verify/:credentialId" element={<Verify />} />
          <Route path="/change-password" element={P(<ChangePassword />)} />
          <Route path="/choose-track" element={P(<ChooseTrack />)} />
          <Route path="/executive/sessions/:sessionId" element={P(<ExecutiveSession />)} />
          <Route path="/executive/check" element={P(<ExecutiveCheck />)} />
          <Route path="/dashboard" element={P(<Dashboard />)} />
          <Route path="/modules/:moduleId" element={P(<ModuleView />)} />
          <Route path="/lessons/:lessonId" element={P(<LessonReader />)} />
          <Route path="/quiz/:attemptId" element={P(<Quiz />)} />
          <Route path="/glossary" element={P(<Glossary />)} />
          <Route path="/architecture-selector" element={<Navigate to="/learn" replace />} />
          <Route path="/architecture" element={<Navigate to="/learn" replace />} />
          <Route path="/architecture/components/:cid" element={<Navigate to="/learn" replace />} />
          <Route path="/labs/:scenarioId" element={P(<LabPlayer />)} />
          <Route path="/learn" element={P(<Learn />)} />
          <Route path="/project" element={P(<PlantProject />)} />
          <Route path="/practice" element={<Navigate to="/learn" replace />} />
          <Route path="/practice/*" element={<Navigate to="/learn" replace />} />
          <Route path="/notes" element={<Navigate to="/learn" replace />} />
          <Route path="/readiness" element={SHOW_READINESS ? P(<Readiness />) : <Navigate to="/dashboard" replace />} />
          <Route path="/capstone" element={P(<Capstone />)} />
          <Route path="/certificates" element={P(<Certificates />)} />
          <Route path="/certificates/:credentialId" element={P(<CertificateView />)} />
          <Route path="/admin" element={P(<Admin />, true)} />
          <Route path="/admin/editions/:slug" element={P(<EditionEditor />, true)} />
        </Routes>
      </div>
      <Toaster theme={theme} position="top-center" />
    </>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandProvider>
          <Shell />
        </BrandProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
